import { ref, computed, readonly } from 'vue'
import type { ShippingAddressesDto } from '@b2ag/module-universcoop-interfaces'
import {
  getLocalStorageJSON,
  removeLocalStorage,
  setLocalStorageJSON,
  type StorageKey,
} from '@b2ag/utils/src/localStorage'
import { provideMembershipApiClient } from '../services'

const SHIPPING_ADDRESS_KEY: StorageKey<ShippingAddressesDto> = 'current_address'

// Singleton state
let singl_instance: ReturnType<typeof createAddressesComposable> | null = null

/**
 * @internal
 * Reset the singleton instance - only for testing purposes
 */
export function resetSingleton() {
  singl_instance = null
}

function hasCityCode(address: ShippingAddressesDto): boolean {
  return !!address.city_code
}

function createAddressesComposable() {
  const savedAddress = getLocalStorageJSON(SHIPPING_ADDRESS_KEY)

  // Reactive state
  const addresses = ref<ShippingAddressesDto[]>(savedAddress ? [savedAddress] : [])
  const currentAddress = ref<ShippingAddressesDto | undefined>(savedAddress ?? undefined)

  // Computed getters
  const addressesWithCityCode = computed(() => addresses.value.filter(hasCityCode))
  const currentCityCode = computed(() => currentAddress.value?.city_code)
  const getAddresses = computed(() => addresses.value)

  // Helper computed for compatibility with existing useShippingAddress
  const shippingAddress = computed(() => currentAddress.value)
  const isValid = computed(() => !!currentAddress.value)
  const asText = computed(() => `${currentAddress.value?.zip_code} - ${currentAddress.value?.city}`)

  // Actions
  const setCurrentAddress = (address: ShippingAddressesDto) => {
    currentAddress.value = address
    if (address) {
      setLocalStorageJSON(SHIPPING_ADDRESS_KEY, address)
    }
  }

  const deleteCurrentAddress = () => {
    currentAddress.value = undefined
    removeLocalStorage(SHIPPING_ADDRESS_KEY)
  }

  const setAddresses = (newAddresses: ShippingAddressesDto[] = []) => {
    addresses.value = newAddresses

    if (
      !currentAddress.value ||
      (currentAddress.value && !newAddresses.some((address) => address.id === currentAddress.value?.id))
    ) {
      const addressWithCityCode = newAddresses.find(hasCityCode)
      if (addressWithCityCode) {
        setCurrentAddress(addressWithCityCode)
      } else {
        deleteCurrentAddress()
      }
    }
  }

  const fetchAddresses = async (partnerId: number, membershipCode: string) => {
    try {
      const membershipApiClient = provideMembershipApiClient()
      const fetchedAddresses = await membershipApiClient
        .findShippingAddressesForAMembershipAndAPartner(partnerId, membershipCode)
        .catch(() => [])
      setAddresses(fetchedAddresses)
    } catch (_error) {
      setAddresses([])
    }
  }

  return {
    // State
    addresses: readonly(addresses),
    currentAddress: readonly(currentAddress),

    // Computed getters
    addressesWithCityCode,
    currentCityCode,
    getAddresses,

    // Compatibility with existing useShippingAddress
    shippingAddress,
    isValid,
    asText,

    // Actions
    setCurrentAddress,
    deleteCurrentAddress,
    setAddresses,
    fetchAddresses,
  }
}

export function useAddresses() {
  if (!singl_instance) {
    singl_instance = createAddressesComposable()
  }
  return singl_instance
}

// Export default for backward compatibility
export default useAddresses
